export type DiffOverviewMarkerType = 'added' | 'removed' | 'both'

export interface DiffOverviewMarker {
  ratio: number
  type: DiffOverviewMarkerType
}

type DiffChange = { value: string; added?: boolean; removed?: boolean }

function isWhitespaceOnly(line: string): boolean {
  return !line || line.trim().length === 0
}

export function changeIncludesLine(
  changes: DiffChange[],
  kind: 'added' | 'removed',
  line: string
): boolean {
  if (isWhitespaceOnly(line)) return false
  const trimmed = line.trim()
  return changes.some((c) => {
    if (kind === 'added' ? !c.added : !c.removed) return false
    return c.value
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0)
      .includes(trimmed)
  })
}

function mergeMarker(
  byRatio: Map<number, DiffOverviewMarkerType>,
  lineIndex: number,
  totalLines: number,
  type: DiffOverviewMarkerType
): void {
  const ratio = Math.min(1, (lineIndex + 0.5) / totalLines)
  const key = Math.round(ratio * 10000)
  const existing = byRatio.get(key)
  if (!existing) {
    byRatio.set(key, type)
    return
  }
  if (existing !== type) {
    byRatio.set(key, 'both')
  }
}

export function buildSplitDiffOverviewMarkers(
  normalizedOriginal: string,
  normalizedProposed: string,
  changes: DiffChange[]
): DiffOverviewMarker[] {
  const origLines = normalizedOriginal.split('\n')
  const propLines = normalizedProposed.split('\n')
  const totalLines = Math.max(origLines.length, propLines.length, 1)
  const byRatio = new Map<number, DiffOverviewMarkerType>()

  origLines.forEach((line, idx) => {
    if (changeIncludesLine(changes, 'removed', line)) {
      mergeMarker(byRatio, idx, totalLines, 'removed')
    }
  })

  propLines.forEach((line, idx) => {
    if (changeIncludesLine(changes, 'added', line)) {
      mergeMarker(byRatio, idx, totalLines, 'added')
    }
  })

  return Array.from(byRatio.entries())
    .map(([key, type]) => ({ ratio: key / 10000, type }))
    .sort((a, b) => a.ratio - b.ratio)
}

export function buildUnifiedDiffOverviewMarkers(changes: DiffChange[]): DiffOverviewMarker[] {
  let totalLines = 0
  for (const change of changes) {
    totalLines += change.value.split('\n').length
  }
  totalLines = Math.max(totalLines, 1)

  const markers: DiffOverviewMarker[] = []
  let lineIndex = 0

  for (const change of changes) {
    const lines = change.value.split('\n')
    for (const line of lines) {
      if (change.added && !isWhitespaceOnly(line)) {
        markers.push({ ratio: (lineIndex + 0.5) / totalLines, type: 'added' })
      } else if (change.removed && !isWhitespaceOnly(line)) {
        markers.push({ ratio: (lineIndex + 0.5) / totalLines, type: 'removed' })
      }
      lineIndex += 1
    }
  }

  return markers
}

/**
 * Overview markers for CodeMirror gutter change highlights (1-based line numbers).
 */
export function buildChangedLinesOverviewMarkers(
  changed: { added?: number[]; removed?: number[]; modified?: number[] } | null | undefined,
  totalLines: number,
): DiffOverviewMarker[] {
  if (!changed) return []
  const total = Math.max(totalLines, 1)
  const byRatio = new Map<number, DiffOverviewMarkerType>()

  for (const lineNumber of changed.added ?? []) {
    if (lineNumber < 1) continue
    mergeMarker(byRatio, lineNumber - 1, total, 'added')
  }
  for (const lineNumber of changed.removed ?? []) {
    if (lineNumber < 1) continue
    mergeMarker(byRatio, lineNumber - 1, total, 'removed')
  }
  for (const lineNumber of changed.modified ?? []) {
    if (lineNumber < 1) continue
    mergeMarker(byRatio, lineNumber - 1, total, 'both')
  }

  return Array.from(byRatio.entries())
    .map(([key, type]) => ({ ratio: key / 10000, type }))
    .sort((a, b) => a.ratio - b.ratio)
}
